import { useState, useMemo } from "react";
import { Row, Col, Badge } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import FormGroupTile from "../../../components/core/basic/FormGroupTile";
import { useFetch } from "../../../hooks/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../utils/api";
import moment from "moment";
import WeeklySeasonalityChart from "./WeeklySeasonalityChart";

import { formatCurrency, formatPercent } from "../../../utils/formatNumber";
import { successToast, errorToast } from "../../../utils/toasts";
import { useMediaQuery } from "@reactuses/core";
import { useSimpleCan } from "../../../config/casl/casl";

const DAYS_IN_WEEK = 7;
const DEFAULT_BUDGET_MULTIPLIER = 1;
const DEFAULT_MULTIPLIER_OBJECT = {
  0: DEFAULT_BUDGET_MULTIPLIER,
  1: DEFAULT_BUDGET_MULTIPLIER,
  2: DEFAULT_BUDGET_MULTIPLIER,
  3: DEFAULT_BUDGET_MULTIPLIER,
  4: DEFAULT_BUDGET_MULTIPLIER,
  5: DEFAULT_BUDGET_MULTIPLIER,
  6: DEFAULT_BUDGET_MULTIPLIER,
};
const SEASONALITY_VALUES = {
  DEFAULT: 0,
  AUTO: 1,
  MANUAL: 2,
};

const Seasonality = ({ categoryId, adPlanData, adPlanParams, marketPlace, isBulkActionForm, customOnChange }) => {
  const { t } = useTranslation(["advertising", "common"]);
  const userCanModifyAds = useSimpleCan("update", "Ad");

  const SEASONALITY_MULTIPLIER_OPTIONS = useMemo(
    () => ({
      DEFAULT: {
        label: t(($) => $.advertising["Default"]),
        value: SEASONALITY_VALUES.DEFAULT,
        description: t(
          ($) =>
            $.advertising[
              "When using the default strategy, Trellis media plans move budget from common low conversion days to common high conversion days (Tuesday through Thursday, Friday through Monday, respectively). Your products sales trend may not match this pattern and the manual or auto strategy may be a better fit."
            ],
        ),
      },
      AUTO: {
        label: t(($) => $.advertising["Auto"]),
        value: SEASONALITY_VALUES.AUTO,
        description: t(
          ($) =>
            $.advertising[
              "When using the auto strategy, Trellis uses historical sales data to determine your category's weekday sales trend and will automatically adjust the budget accordingly."
            ],
        ),
      },
      MANUAL: {
        label: t(($) => $.advertising["Manual"]),
        value: SEASONALITY_VALUES.MANUAL,
        description: t(($) => $.advertising["The manual strategy allows you to overwrite daily budget modifiers."]),
      },
    }),
    [t],
  );

  const seasonalityMultiplierOptionsArray = useMemo(
    () => Object.values(SEASONALITY_MULTIPLIER_OPTIONS),
    [SEASONALITY_MULTIPLIER_OPTIONS],
  );

  const formatDefaultMultiplierValues = (multipliers, strategy) => {
    const formattedMultipliersObject = {};

    // If manual strategy selected and weekday multipliers save, use those as defaults, otherwise revert
    // to auto multipliers for presets
    if (!multipliers?.length || strategy !== SEASONALITY_VALUES.MANUAL) {
      return {};
    }

    multipliers.forEach((multiplier) => {
      formattedMultipliersObject[multiplier.weekday] = multiplier.multiplier;
    });
    return formattedMultipliersObject;
  };

  const [multiplierStrategy, setMultiplierStrategy] = useState(
    seasonalityMultiplierOptionsArray.find((o) => o.value === adPlanParams.weekPartingMethod) ??
      SEASONALITY_MULTIPLIER_OPTIONS.DEFAULT,
  );
  const [manualWeekdayMultiplers, setManualWeekdayMultipliers] = useState(
    formatDefaultMultiplierValues(adPlanParams?.weekPartingMultipliers, adPlanParams?.weekPartingMethod),
  );

  const queryClient = useQueryClient();
  const isVerticalLayout = useMediaQuery("(max-width:1600px)");

  const seasonalCategoryQuery = useFetch(
    ["seasonality", categoryId],
    `/api/seasonality`,
    { category: parseInt(categoryId) },
    {
      select: (res) => res.data?.results ?? [],
      enabled: !!categoryId,
    },
  );
  let isSeasonalCategory = false;
  if (seasonalCategoryQuery.isSuccess) {
    isSeasonalCategory = JSON.parse(seasonalCategoryQuery.data[0]?.weekly_seasons ?? "[]")?.some(
      (day) => day.multiplier !== 1,
    );
  }

  const parsedWeeklySeasonalityData = useMemo(() => {
    if (seasonalCategoryQuery.data?.[0]?.weekly_seasons) {
      return JSON.parse(seasonalCategoryQuery.data?.[0]?.weekly_seasons)?.map((day) => ({
        ...day,
        // Seasonality data treats Monday as the first weekday, versus moment treating Sunday as the first weekday
        // Below offset by 1 is so data and labels line up in chart and range slider
        formattedWeekday: moment.weekdays(day.weekday + 1),
      }));
    }

    if (!categoryId || isBulkActionForm) {
      return Array.from({ length: 7 }).map((_, weekday) => ({
        weekday,
        multiplier: 1,
        formattedWeekday: moment.weekdays(weekday + 1),
      }));
    }
  }, [seasonalCategoryQuery.data, categoryId, isBulkActionForm]);
  const dailyMultiplierData = useMemo(() => {
    if (multiplierStrategy.value === SEASONALITY_VALUES.AUTO) {
      return seasonalCategoryQuery.data?.[0]?.weekly_seasons ?
          JSON.parse(seasonalCategoryQuery.data[0].weekly_seasons).map((day) => ({
            ...day,
            formattedWeekday: moment.weekdays(day.weekday + 1),
          }))
        : Object.keys(DEFAULT_MULTIPLIER_OBJECT).map((weekday) => ({
            weekday: parseInt(weekday),
            multiplier: DEFAULT_MULTIPLIER_OBJECT[weekday],
            formattedWeekday: moment.weekdays(parseInt(weekday) + 1),
          }));
    }

    if (multiplierStrategy.value === SEASONALITY_VALUES.MANUAL) {
      return parsedWeeklySeasonalityData?.map((day) => ({
        ...day,
        multiplier: manualWeekdayMultiplers[day.weekday] ?? day.multiplier,
      }));
    }

    // Default strategy
    return seasonalCategoryQuery.data?.[0]?.weekly_seasons ?
        JSON.parse(seasonalCategoryQuery.data[0].weekly_seasons).map((day) => ({
          ...day,
          multiplier: DEFAULT_BUDGET_MULTIPLIER,
          formattedWeekday: moment.weekdays(day.weekday + 1),
        }))
      : Object.keys(DEFAULT_MULTIPLIER_OBJECT).map((weekday) => ({
          weekday: parseInt(weekday),
          multiplier: DEFAULT_BUDGET_MULTIPLIER,
          formattedWeekday: moment.weekdays(parseInt(weekday) + 1),
        }));
  }, [multiplierStrategy.value, parsedWeeklySeasonalityData, manualWeekdayMultiplers, seasonalCategoryQuery.data]);

  const updateHandler = useMutation({
    mutationFn: async (body) => {
      return await api.put("/api/gvads/advproduct/", body);
    },

    onSuccess: () => {
      successToast({
        message: t(($) => $.advertising["Updating Media Plan"]),
      });
      queryClient.invalidateQueries({
        queryKey: ["seasonality", categoryId],
      });
    },

    onError: (error) => {
      errorToast({
        message: t(($) => $.advertising["Failed to Update Media Plan"]),
        error,
      });
    },
  });

  const handleSubmit = () => {
    const bodyData = {
      id: adPlanData.id,
      custom_api_request: "update_advertisement",
      state: adPlanData.state,
      ad_control_fields: {
        week_parting_method: multiplierStrategy.value,
        week_parting_multipliers: dailyMultiplierData.map((day) => {
          const multiplier =
            [SEASONALITY_VALUES.AUTO, SEASONALITY_VALUES.MANUAL].includes(multiplierStrategy.value) ?
              day.multiplier
            : DEFAULT_BUDGET_MULTIPLIER;
          return { multiplier, weekday: day.weekday };
        }),
      },
    };

    updateHandler.mutate(bodyData);
  };

  const weeklyMultiplierTotal = dailyMultiplierData?.reduce((acc, day) => acc + day.multiplier, 0);
  const calculatedWeeklyConfiguredBudget = formatCurrency(
    multiplierStrategy.value === SEASONALITY_VALUES.DEFAULT ?
      adPlanParams?.dailyBudget * DEFAULT_BUDGET_MULTIPLIER * DAYS_IN_WEEK
    : adPlanParams?.dailyBudget * weeklyMultiplierTotal,
    {
      marketPlace: marketPlace,
    },
  );

  return (
    <div>
      <Row>
        <Col xs={isBulkActionForm || isVerticalLayout ? 12 : 3} className="p-4">
          <Row>
            <Col xs={12} className="mx-auto py-2 fs-3 px-5">
              <h5 className="fw-bold text-center pb-4 pt-1">{t(($) => $.advertising["Budget Modifier Strategy"])}</h5>
              <Row>
                <Col xs={12} md={isBulkActionForm || isVerticalLayout ? { span: 4, offset: 4 } : 8}>
                  <FormGroupTile
                    type="select"
                    multi={false}
                    handleChildFormElement={(key, value) => {
                      setMultiplierStrategy(value);

                      let nextManual = manualWeekdayMultiplers;
                      if (value.value === SEASONALITY_VALUES.MANUAL) {
                        if (!Object.keys(manualWeekdayMultiplers).length) {
                          nextManual =
                            parsedWeeklySeasonalityData?.reduce((acc, day) => {
                              acc[day.weekday] = day.multiplier;
                              return acc;
                            }, {}) ?? DEFAULT_MULTIPLIER_OBJECT;

                          setManualWeekdayMultipliers(nextManual);
                        }
                      }

                      if (customOnChange) {
                        let multipliersPayload = null;

                        if (value.value === SEASONALITY_VALUES.MANUAL) {
                          multipliersPayload = Object.entries(nextManual).map(([weekday, multiplier]) => ({
                            weekday: Number(weekday),
                            multiplier: Number(multiplier),
                          }));
                        }

                        if (value.value === SEASONALITY_VALUES.AUTO) {
                          multipliersPayload =
                            seasonalCategoryQuery.data?.[0]?.weekly_seasons ?
                              JSON.parse(seasonalCategoryQuery.data[0].weekly_seasons)
                            : Object.entries(DEFAULT_MULTIPLIER_OBJECT).map(([weekday, multiplier]) => ({
                                weekday: parseInt(weekday),
                                multiplier,
                              }));
                        }

                        customOnChange({
                          week_parting_method: value.value,
                          week_parting_multipliers: multipliersPayload,
                        });
                      }
                    }}
                    defaultValue={multiplierStrategy}
                    options={seasonalityMultiplierOptionsArray}
                    disabled={!userCanModifyAds}
                  />
                </Col>
                {!isBulkActionForm && (
                  <Col xs={12} lg={4}>
                    <button
                      className="adplan_button px-5"
                      onClick={() => {
                        handleSubmit();
                      }}
                      style={{ transform: "none" }}
                      disabled={adPlanData.serving_status === "ENDED" || !userCanModifyAds}
                    >
                      {t(($) => $.common.actions.Save)}
                    </button>
                  </Col>
                )}
              </Row>
            </Col>
            <Col xs={12} md={isBulkActionForm || isVerticalLayout ? { span: 6, offset: 3 } : 12}>
              {!isSeasonalCategory && !seasonalCategoryQuery.isLoading && (
                <p className="text-center mx-auto fs-5 pt-5 px-3">
                  {!categoryId ?
                    t(($) => $.advertising["Filter to a category to see seasonality."])
                  : t(($) => $.advertising["No seasonality has been detected for this category."])}
                </p>
              )}
              <p
                className={`text-center mx-auto fs-5 ${
                  isSeasonalCategory || seasonalCategoryQuery.isLoading ? "pt-5" : ""
                } px-3`}
              >
                {multiplierStrategy.description}
              </p>
            </Col>
          </Row>
        </Col>
        {!(isBulkActionForm && !categoryId && multiplierStrategy.value === SEASONALITY_VALUES.AUTO) && (
          <Col xs={isBulkActionForm || isVerticalLayout ? 12 : 9} className="py-3 px-5">
            <Row>
              <Col xs={12} className="py-3 px-5 ">
                <WeeklySeasonalityChart
                  data={dailyMultiplierData?.map((d) => ({
                    ...d,
                    multiplier:
                      // Format decimal values representing percents to be human readable
                      multiplierStrategy.value === SEASONALITY_VALUES.DEFAULT ? 100 : d.multiplier * 100,
                  }))}
                  isLoading={seasonalCategoryQuery.isLoading}
                  dailyBudget={adPlanParams?.dailyBudget}
                  marketPlace={marketPlace}
                />
              </Col>
            </Row>
            <h5 className="fw-bold text-center pt-1">{t(($) => $.advertising["Budget Modifiers"])}</h5>
            <Row>
              <Col xs={2} />
              <Col xs={8}>
                {isBulkActionForm ?
                  <p className="fs-5 text-muted text-center">
                    {weeklyMultiplierTotal - DEFAULT_BUDGET_MULTIPLIER * DAYS_IN_WEEK > 0 ?
                      t(($) => $.advertising["Configured weekly average budget is over by {percent}"], {
                        percent: formatPercent((weeklyMultiplierTotal - DEFAULT_BUDGET_MULTIPLIER * DAYS_IN_WEEK) / 7, {
                          decimalPlaces: 0,
                        }),
                      })
                    : weeklyMultiplierTotal - DEFAULT_BUDGET_MULTIPLIER * DAYS_IN_WEEK < 0 ?
                      t(($) => $.advertising["Configured weekly average budget is under by {percent}"], {
                        percent: formatPercent(
                          Math.abs(weeklyMultiplierTotal - DEFAULT_BUDGET_MULTIPLIER * DAYS_IN_WEEK) / 7,
                          {
                            decimalPlaces: 0,
                          },
                        ),
                      })
                    : ""}
                  </p>
                : <>
                    <p className="fs-5 text-muted text-center">
                      {t(($) => $.advertising["Original weekly average budget is {amount}"], {
                        amount: formatCurrency(adPlanParams?.dailyBudget * DEFAULT_BUDGET_MULTIPLIER * DAYS_IN_WEEK, {
                          marketPlace: marketPlace,
                        }),
                      })}
                    </p>
                    <p className="fs-5 text-muted text-center">
                      {t(($) => $.advertising["Configured weekly average budget is {amount}"], {
                        amount: calculatedWeeklyConfiguredBudget,
                      })}
                      <span>
                        {(
                          multiplierStrategy.value === SEASONALITY_VALUES.DEFAULT ||
                          formatPercent(
                            Math.abs((weeklyMultiplierTotal - DEFAULT_BUDGET_MULTIPLIER * DAYS_IN_WEEK) / 7),
                            {
                              decimalPlaces: 0,
                            },
                          ) === "0%"
                        ) ?
                          ""
                        : weeklyMultiplierTotal - DEFAULT_BUDGET_MULTIPLIER * DAYS_IN_WEEK > 0 ?
                          t(($) => $.advertising[", over by {percent}"], {
                            percent: formatPercent(
                              (weeklyMultiplierTotal - DEFAULT_BUDGET_MULTIPLIER * DAYS_IN_WEEK) / 7,
                              {
                                decimalPlaces: 0,
                              },
                            ),
                          })
                        : weeklyMultiplierTotal - DEFAULT_BUDGET_MULTIPLIER * DAYS_IN_WEEK < 0 ?
                          t(($) => $.advertising[", under by {percent}"], {
                            percent: formatPercent(
                              Math.abs(weeklyMultiplierTotal - DEFAULT_BUDGET_MULTIPLIER * DAYS_IN_WEEK) / 7,
                              {
                                decimalPlaces: 0,
                              },
                            ),
                          })
                        : ""}
                      </span>
                    </p>
                  </>
                }
              </Col>

              <Col xs={2}>
                <div className="w-100 h-100 d-flex justify-content-end align-items-center">
                  <button
                    className="adplan_button p-3 fs-5"
                    disabled={
                      multiplierStrategy.value !== SEASONALITY_VALUES.MANUAL ||
                      adPlanData.serving_status === "ENDED" ||
                      !userCanModifyAds
                    }
                    onClick={() => {
                      setManualWeekdayMultipliers(DEFAULT_MULTIPLIER_OBJECT);
                      customOnChange?.({
                        week_parting_method: SEASONALITY_VALUES.MANUAL,
                        week_parting_multipliers: Object.entries(DEFAULT_MULTIPLIER_OBJECT).map(
                          ([weekday, multiplier]) => ({
                            weekday: Number(weekday),
                            multiplier,
                          }),
                        ),
                      });
                    }}
                  >
                    {t(($) => $.advertising["Reset"])}
                  </button>
                </div>
              </Col>
            </Row>

            <div
              className="p-5 d-flex justify-content-center align-items-center"
              style={{ marginTop: "80px", marginLeft: "70px", maxWidth: "100%" }}
            >
              {dailyMultiplierData?.map((day, i) => {
                return (
                  <div key={`${day.weekday}-${day.multiplier}-${i}`} className="text-center w-100">
                    <div style={{ position: "relative" }}>
                      <div className="text-center w-100" style={{ position: "absolute", top: "-95px" }}>
                        <Badge
                          className="badge bg-trellis-fuchsia fs-5 py-2"
                          style={{
                            borderRadius: "5px",
                          }}
                          pill
                        >
                          {formatPercent(
                            multiplierStrategy.value === SEASONALITY_VALUES.DEFAULT ?
                              DEFAULT_BUDGET_MULTIPLIER
                            : day.multiplier,
                            {
                              decimalPlaces: 0,
                            },
                          )}{" "}
                          <span>
                            {!isBulkActionForm &&
                              `- ${formatCurrency(
                                adPlanParams?.dailyBudget *
                                  (multiplierStrategy.value === SEASONALITY_VALUES.DEFAULT ?
                                    DEFAULT_BUDGET_MULTIPLIER
                                  : day.multiplier),
                                {
                                  marketPlace: marketPlace,
                                },
                              )}`}
                          </span>
                        </Badge>
                      </div>
                      <div style={{ height: "85px" }}>
                        <input
                          type="range"
                          value={
                            multiplierStrategy.value === SEASONALITY_VALUES.DEFAULT ?
                              DEFAULT_BUDGET_MULTIPLIER
                            : day.multiplier
                          }
                          style={{
                            transform: "rotate(270deg)",
                          }}
                          data-weekday={day.weekday}
                          onChange={(e) => {
                            const weekday = e.target.dataset.weekday;
                            const updatedMultiplier = parseFloat(e.target.value);
                            setManualWeekdayMultipliers((prev) => ({
                              ...prev,
                              [weekday]: updatedMultiplier,
                            }));
                            if (customOnChange) {
                              const newState = { ...manualWeekdayMultiplers, [weekday]: updatedMultiplier };
                              customOnChange({
                                week_parting_method: multiplierStrategy.value,
                                week_parting_multipliers: Object.entries(newState).map(([weekday, multiplier]) => ({
                                  weekday: parseInt(weekday),
                                  multiplier: multiplier,
                                })),
                              });
                            }
                          }}
                          min={0}
                          max={1.5}
                          step={0.01}
                          disabled={multiplierStrategy.value !== SEASONALITY_VALUES.MANUAL || !userCanModifyAds}
                        />
                      </div>
                    </div>
                    <p className="text-center">{day.formattedWeekday}</p>
                  </div>
                );
              })}
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default Seasonality;
